import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Bookmark, FileText, Bot, Trash2, EyeOff, Layers, ShieldQuestion } from "lucide-react";
import { fetchDeepDive, api } from "../lib/api";
import { earnCoins } from "../lib/coins";
import { isDeepDiveSaved, toggleDeepDiveSaved } from "../lib/bookmarks";
import { isAdmin, adminHeader } from "../lib/admin";
import { renderMarkdown } from "../lib/markdown";
import CredibilityBadge from "../components/CredibilityBadge";
import Comments from "../components/Comments";
import WitnessStatements from "../components/WitnessStatements";
import PredictionPanel from "../components/PredictionPanel";
import CommunityFacts from "../components/CommunityFacts";
import BeliefSlider from "../components/BeliefSlider";
import CrossReferences from "../components/CrossReferences";
import PrintButton from "../components/PrintButton";
import ReactionButtons from "../components/ReactionButtons";
import ShareButton from "../components/ShareButton";
import CosignButton from "../components/CosignButton";
import PrevNextNav from "../components/PrevNextNav";
import OpenInTiesButton from "../components/OpenInTiesButton";
import StrongestLinkShareButton from "../components/StrongestLinkShareButton";
import CiteInThreadButton from "../components/CiteInThreadButton";
import ArtifactExtras from "../components/ArtifactExtras";
import { useTrackPageView } from "../hooks/useAnalytics";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function AIDossierPage() {
  const { id } = useParams();
  useTrackPageView(`/ai-dossier/${id}`, { target_type: "ai_dossier", target_id: id });
  const navigate = useNavigate();
  const [dive, setDive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const [admin, setAdmin] = useState(isAdmin());

  useEffect(() => {
    const h = () => setAdmin(isAdmin());
    window.addEventListener("admin-changed", h);
    return () => window.removeEventListener("admin-changed", h);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchDeepDive(id)
      .then((d) => {
        setDive(d);
        setSaved(isDeepDiveSaved(id));
        // Coin award — rate-limited server-side; failures silent.
        earnCoins("browse_dossier", `ai:${id}`).then((r) => {
          if (r?.awarded > 0) window.dispatchEvent(new Event("coins-changed"));
        });
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const onSave = () => {
    const next = toggleDeepDiveSaved(id);
    setSaved(next);
    toast.success(next ? "Saved to your library" : "Removed from your library");
  };

  const onAdminDelete = async () => {
    if (!window.confirm("Permanently DELETE this AI dossier? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/deep-dive/${id}`, { headers: adminHeader() });
      toast.success("Dossier deleted");
      navigate("/archive");
    } catch {
      toast.error("Delete failed");
    }
  };

  const onAdminUnpublish = async () => {
    if (!window.confirm("Remove this AI dossier from the public archive?")) return;
    try {
      await api.post(`/admin/deep-dive/${id}/unpublish`, null, { headers: adminHeader() });
      toast.success("Dossier unpublished");
      navigate("/archive");
    } catch {
      toast.error("Unpublish failed");
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-24">
        <div className="font-mono text-xs tracking-wider text-zinc-500">LOADING FILE...</div>
      </div>
    );
  }
  if (err || !dive) {
    return (
      <div data-testid="ai-dossier-not-found" className="max-w-5xl mx-auto px-4 sm:px-8 py-24">
        <div className="font-mono text-xs tracking-wider text-[#ff3b30] mb-4">AI FILE NOT FOUND</div>
        <Link to="/archive" className="font-mono text-sm text-white underline">← Return to Archive</Link>
      </div>
    );
  }

  // Public view: only show analyst messages (researcher questions are private)
  const allMessages = dive.messages && dive.messages.length > 0
    ? dive.messages
    : [{ role: "assistant", content: dive.report, timestamp: dive.created_at }];
  const messages = allMessages.filter((m) => m.role === "assistant");

  const credibility = dive.inferred_credibility || "ai_compiled";

  return (
    <article data-testid="ai-dossier-page" className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <Link to="/archive" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-zinc-500 hover:text-white mb-10">
        <ArrowLeft size={12} /> ARCHIVE INDEX
      </Link>

      <div className="bg-[#0F0F0F] border-t-4 border-t-[#00ff88] border-x border-b border-white/10 p-6 md:p-10">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10 flex-wrap gap-3">
          <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] text-zinc-500">
            <FileText size={12} />
            DOSSIER — AI-COMPILED // {messages.length} MESSAGES // ID {dive.id.slice(0, 8).toUpperCase()}
          </div>
          <button
            onClick={onSave}
            data-testid="ai-dossier-save"
            className={`inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
              saved
                ? "border-[#ffd60a]/50 text-[#ffd60a] bg-[#ffd60a]/5"
                : "border-white/15 text-zinc-400 hover:text-[#ffd60a] hover:border-[#ffd60a]/50"
            }`}
          >
            <Bookmark size={12} fill={saved ? "#ffd60a" : "none"} />
            {saved ? "Saved" : "Save"}
          </button>
          <OpenInTiesButton kind="ai_dossier" ref={dive.id} variant="ghost" size="sm" testid="ai-dossier-open-ties" />
          <StrongestLinkShareButton kind="ai_dossier" ref={dive.id} testid="ai-dossier-share-card" />
          <CiteInThreadButton kind="ai_dossier" ref={dive.id} title={dive.topic} url={`/ai-dossier/${dive.id}`} testid="ai-dossier-cite-thread" />
          <PrintButton />
          {admin && (
            <>
              <button
                onClick={onAdminUnpublish}
                data-testid="admin-unpublish"
                className="inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 border-white/15 hover:text-[#ffd60a] hover:border-[#ffd60a]/50 transition-colors"
              >
                <EyeOff size={11} /> Unpublish
              </button>
              <button
                onClick={onAdminDelete}
                data-testid="admin-delete"
                className="inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 border-white/15 hover:text-[#ff3b30] hover:border-[#ff3b30]/50 transition-colors"
              >
                <Trash2 size={11} /> Delete
              </button>
            </>
          )}
        </div>

        <h1 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl text-white leading-[0.95] mb-4">
          {dive.topic}
        </h1>

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <CredibilityBadge level="ai_compiled" />
          {dive.mode === "eli5" && (
            <span data-testid="dive-mode-badge"
              className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[#00ff88]/60 bg-[#00ff88]/10 text-[#00ff88] font-mono text-[9px] tracking-[0.2em] uppercase">
              ● ELI5 — PLAIN-ENGLISH EXPLAINER
            </span>
          )}
          {dive.mode === "social" && (
            <span data-testid="dive-mode-badge"
              className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[#ec4899]/60 bg-[#ec4899]/10 text-[#ec4899] font-mono text-[9px] tracking-[0.2em] uppercase">
              ● SOCIAL POST PROMPTS
            </span>
          )}
          <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
            COMPILED — {new Date(dive.created_at).toISOString().slice(0, 10)}
          </span>
        </div>

        <div className="border border-[#ff3b30]/30 bg-[#ff3b30]/5 px-4 py-3 mb-6 font-mono text-[10px] tracking-wider text-[#ff3b30] leading-relaxed flex items-center justify-between flex-wrap gap-3">
          <span>NOTICE — AI-compiled brief. Verify all sources independently before citing. AI can hallucinate URLs and dates.</span>
          {dive.skeptical && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[#ff3b30]/50 bg-[#ff3b30]/10 text-[#ff3b30] font-mono text-[9px] tracking-[0.2em]">
              ⚠ SKEPTICAL MODE
            </span>
          )}
        </div>

        {dive.tavily_sources && dive.tavily_sources.length > 0 && (
          <details
            data-testid="tavily-sources-panel"
            className="mb-6 border border-[#60a5fa]/30 bg-[#0a0a0a] p-4"
          >
            <summary className="cursor-pointer font-mono text-[10px] tracking-[0.25em] text-[#60a5fa] flex items-center gap-2 hover:text-white transition-colors">
              ▸ LIVE WEB SOURCES — {dive.tavily_sources.length} · TAVILY · PULLED AT DIVE TIME
            </summary>
            <ul className="mt-3 space-y-3">
              {dive.tavily_sources.map((s, i) => (
                <li key={`${s.url}-${i}`} className="border-l-2 border-[#60a5fa]/40 pl-3">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block font-mono text-[11px] tracking-wide text-[#60a5fa] hover:underline break-all"
                  >
                    {s.title || s.url}
                  </a>
                  <div className="flex items-center gap-2 mt-0.5 font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase">
                    {s.source && <span>{s.source}</span>}
                    {s.published_date && <span>· {s.published_date}</span>}
                  </div>
                  {s.content && (
                    <p className="mt-1 text-[12px] text-zinc-400 leading-relaxed line-clamp-4">
                      {s.content.length > 280 ? s.content.slice(0, 280).trimEnd() + "…" : s.content}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}

        {dive.citations && dive.citations.length > 0 && (
          <details
            data-testid="citations-panel"
            className="mb-6 border border-[#00ff88]/30 bg-[#0a0a0a] p-4"
          >
            <summary className="cursor-pointer font-mono text-[10px] tracking-[0.25em] text-[#00ff88] flex items-center gap-2">
              SOURCES CITED — {dive.citations.length}
            </summary>
            <ol className="mt-3 space-y-1.5 list-decimal list-inside font-mono text-[11px] text-zinc-300">
              {dive.citations.map((u, i) => (
                <li key={i} className="break-all">
                  <a href={u} target="_blank" rel="noopener noreferrer" className="text-[#60a5fa] hover:underline">
                    {u}
                  </a>
                </li>
              ))}
            </ol>
          </details>
        )}

        <div className="mb-8 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <ReactionButtons targetType="ai_dossier" targetId={dive.id} />
            {/* Co-signs surface only on published, public AI dossiers */}
            {dive.is_published && (
              <CosignButton targetType="deep_dive" targetId={dive.id} authorId={dive.user_id} />
            )}
          </div>
          <ShareButton title={dive.topic} testId="aidive-share" />
        </div>

        {/* Skeptical-pair cross-link */}
        {dive.skeptical_pair_id && (
          <Link
            to={`/ai-dossier/${dive.skeptical_pair_id}`}
            data-testid="skeptical-pair-link"
            className={`flex items-center justify-between gap-3 mb-6 px-4 py-3 border transition-colors ${
              dive.skeptical
                ? "border-[#00ff88]/40 bg-[#0a0a0a] hover:border-[#00ff88]"
                : "border-[#ffaa00]/40 bg-[#0a0a0a] hover:border-[#ffaa00]"
            }`}
          >
            <div className="flex items-center gap-3">
              <ShieldQuestion size={14} className={dive.skeptical ? "text-[#00ff88]" : "text-[#ffaa00]"} />
              <span className={`font-mono text-[11px] tracking-[0.2em] uppercase ${dive.skeptical ? "text-[#00ff88]" : "text-[#ffaa00]"}`}>
                Read the {dive.skeptical ? "STANDARD" : "SKEPTICAL"} counterpart →
              </span>
            </div>
            <span className="font-mono text-[10px] tracking-[0.15em] text-zinc-400">Paired dossier</span>
          </Link>
        )}

        <div className="space-y-6">
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`ai-thread-msg-${i}`}
              className={`border-l-2 pl-5 ${dive.skeptical ? "border-[#ffaa00]" : "border-[#00ff88]"}`}
            >
              <div className={`font-mono text-[10px] tracking-[0.25em] mb-3 flex items-center gap-2 ${dive.skeptical ? "text-[#ffaa00]" : "text-[#00ff88]"}`}>
                <Bot size={11} /> ANALYST{i > 0 ? ` — RESPONSE ${i + 1}` : ""}
              </div>
              <div
                className="markdown text-zinc-300"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }}
              />
            </div>
          ))}
        </div>

        {/* EXPANSIONS — appended depth passes, never overwrite the original. Skip placeholders. */}
        {dive.expansions && dive.expansions.filter((e) => e.status !== "generating" && e.status !== "error" && (e.report || "").length > 0).length > 0 && (
          <div data-testid="expansions-panel" className="mt-10 space-y-8">
            {dive.expansions
              .filter((e) => e.status !== "generating" && e.status !== "error" && (e.report || "").length > 0)
              .map((exp, idx) => (
              <div
                key={idx}
                data-testid={`expansion-${idx + 1}`}
                className="border-l-2 border-[#a78bfa] pl-5"
              >
                <div className="font-mono text-[10px] tracking-[0.25em] mb-3 flex items-center gap-2 text-[#a78bfa]">
                  <Layers size={11} /> EXPANSION PASS {exp.level || idx + 1} —{" "}
                  {exp.generated_at ? new Date(exp.generated_at).toISOString().slice(0, 10) : ""}
                </div>
                <div
                  className="markdown text-zinc-300"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(exp.report || "") }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Skeptical-version button removed from public view — expansion is handled by admin manage page only. */}
      </div>

      <ArtifactExtras kind="ai_dossier" ref={dive.id} subjects={[dive.topic]} />

      <BeliefSlider targetType="ai_dossier" targetId={dive.id} />
      <CommunityFacts targetType="ai_dossier" targetId={dive.id} />
      <CrossReferences targetType="ai_dossier" targetId={dive.id} />
      <PrevNextNav
        type="ai_dossier"
        currentId={dive.id}
        urlBuilder={(id) => `/ai-dossier/${id}`}
        accent={dive.skeptical ? "#ffaa00" : "#00ff88"}
        testIdPrefix="ai-dossier-nav"
      />
      <Comments targetType="ai_dossier" targetId={dive.id} />
      <WitnessStatements targetType="ai_dossier" targetId={dive.id} />
      <PredictionPanel targetType="ai_dossier" targetId={dive.id} />
    </article>
  );
}
